/**
 * Giphy service — platform-agnostic access to the public Giphy GIF API.
 * Consumed by the mobile and web GIF pickers through `useGifSearch`.
 */
import { GIPHY_API_KEY } from '../../config/env';
import {
  GIF_MIME_TYPE,
  GIPHY_API_BASE_URL,
  GIPHY_ENDPOINTS,
  GIPHY_RESULT_LIMIT,
} from '../../constants/giphy';
import type { GifSelection, GiphyGif } from '../../types/gif';

interface GiphyRendition {
  url?: string;
  width?: string;
  height?: string;
}

interface GiphyApiGif {
  id?: string | number;
  title?: string;
  images?: {
    fixed_height?: GiphyRendition;
  };
}

interface GiphyApiResponse {
  data?: GiphyApiGif[];
}

const toPositiveNumber = (value?: string): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
};

const mapGif = (gif: GiphyApiGif): GiphyGif | null => {
  const rendition = gif.images?.fixed_height;
  const url = rendition?.url?.trim();
  if (!gif.id || !url) {
    return null;
  }
  return {
    id: String(gif.id),
    url,
    title: (gif.title || '').trim(),
    width: toPositiveNumber(rendition?.width),
    height: toPositiveNumber(rendition?.height),
  };
};

/** False when no API key is configured — callers should hide/disable the picker. */
const isConfigured = (): boolean => !!GIPHY_API_KEY?.trim();

const buildRequestUrl = (query: string): string => {
  const key = encodeURIComponent(GIPHY_API_KEY.trim());
  const path = query ? GIPHY_ENDPOINTS.SEARCH : GIPHY_ENDPOINTS.TRENDING;
  const searchParam = query ? `&q=${encodeURIComponent(query)}` : '';
  return `${GIPHY_API_BASE_URL}${path}?api_key=${key}&limit=${GIPHY_RESULT_LIMIT}${searchParam}`;
};

/**
 * Fetches trending GIFs, or search results when `query` is non-empty.
 * Throws on network/HTTP failure so callers can surface an error state.
 */
const fetchGifs = async (
  query = '',
  signal?: AbortSignal,
): Promise<GiphyGif[]> => {
  if (!isConfigured()) {
    return [];
  }

  const response = await fetch(
    buildRequestUrl(query.trim()),
    signal ? { signal } : undefined,
  );
  if (!response.ok) {
    throw new Error(`[giphyService] Request failed with status ${response.status}`);
  }

  const body = (await response.json()) as GiphyApiResponse;
  return (body.data ?? [])
    .map(mapGif)
    .filter((gif): gif is GiphyGif => gif !== null);
};

/** Converts a result into the media-asset shape the comment/post/chat senders expect. */
const toGifSelection = (gif: GiphyGif): GifSelection => ({
  uri: gif.url,
  type: GIF_MIME_TYPE,
  fileName: `gif_${gif.id}.gif`,
  fileSize: 0,
});

const giphyService = {
  isConfigured,
  fetchGifs,
  toGifSelection,
};

export default giphyService;
